import React, { useMemo, useState, useEffect } from 'react';
import { CircleQuestionMark } from 'lucide-react';
import { useAppSelector } from 'src/store';
import Modal from 'src/shared/components/modal/Modal';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip/IUCNStatusChip';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
import { CircleAlert } from 'lucide-react';
import Loader from 'src/shared/components/loader/Loader';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import DataTable from 'src/shared/components/data-table/DataTable';
import {
    DataTableColumns,
    DataTableState,
    SortingDirection,
    TableCellStructuredData,
    TableRow,
    TableRowsSortingFunction,
} from 'src/shared/components/data-table/dataTableTypes';
import { Badge } from 'src/shared/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from 'src/shared/components/ui/tabs';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import { confidenceDetails } from 'src/shared/helpers/speciesConfidence';
import { PortfolioSpeciesConfidence } from 'src/shared/types/graph/portfolio';
import TableUpsellSection from 'src/app/portfolio/components/table-upsell-section/TableUpsellSection';
import { modalTablesMergeComparator } from 'src/app/portfolio/helpers/portfolioHelpers';
import DetectionBadge, { DetectionBadgeType } from 'src/app/portfolio/components/detection-badge/DetectionBadge';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { useSiteSpeciesRisk, type SiteSpeciesRiskItem } from '../../hooks/useSiteSpeciesRisk';

/** The raw species behind a row — cells are rendered nodes, so sorting reads this. */
const rowSpecies = (row: TableRow): SiteSpeciesRiskItem | null => {
    const firstCell = row.cells[0];
    if (!firstCell || typeof firstCell !== 'object' || !('data' in firstCell)) {
        return null;
    }
    return ((firstCell as TableCellStructuredData).data as SiteSpeciesRiskItem) ?? null;
};

const sortingBy =
    (compare: (a: SiteSpeciesRiskItem, b: SiteSpeciesRiskItem) => number): TableRowsSortingFunction =>
    (rows, _columnIndex, direction) =>
        [...rows].sort((rowA, rowB) => {
            const a = rowSpecies(rowA);
            const b = rowSpecies(rowB);
            if (!a || !b) {
                return 0;
            }
            return (direction === SortingDirection.DESC ? -1 : 1) * compare(a, b);
        });

const byName = sortingBy((a, b) => (a.commonName || a.scientificName).localeCompare(b.commonName || b.scientificName));
const bySeverity = sortingBy(
    (a, b) =>
        (iucnOrder[(a.iucnRedListCategory || '').toUpperCase()] ?? 99) - (iucnOrder[(b.iucnRedListCategory || '').toUpperCase()] ?? 99)
);
const byConfidence = sortingBy(
    (a, b) =>
        (confidenceOrder[a.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99) -
        (confidenceOrder[b.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99)
);

const tableColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    {
        columnId: 'speciesName',
        title: 'Species name',
        width: '33%',
        isSortable: true,
        sortFn: byName,
    },
    {
        columnId: 'iucnRedListStatus',
        title: 'IUCN Red List status',
        width: '25%',
        titleSlug: 'species-risk-site-iucn-status',
        isSortable: true,
        sortFn: bySeverity,
    },
    {
        columnId: 'confidence',
        title: 'Prediction Confidence',
        width: '10%',
        titleSlug: 'species-risk-site-confidence',
        isSortable: true,
        sortFn: byConfidence,
    },
    {
        columnId: 'nmSamples',
        title: 'NatureMetrics Samples',
        width: '15%',
        titleSlug: 'species-risk-site-nm-samples',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.BookCall ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.BookCall),
            content: <TableUpsellSection type={DetectionBadgeType.BookCall} />,
        },
    },
    {
        columnId: 'uploadedData',
        title: 'Uploaded Sample Data',
        width: '17%',
        titleSlug: 'species-risk-site-uploaded-data',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.UploadData ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.UploadData),
            content: <TableUpsellSection type={DetectionBadgeType.UploadData} />,
        },
    },
];

export type SiteSpecies = {
    scientificName: string;
    commonName?: string | null;
    iucnRedListCategory?: string | null;
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
};

type Props = {
    onClose: () => void;
    siteId: string;
    initialTab: 'iucn' | 'invasives';
};

const confidenceOrder: Record<PortfolioSpeciesConfidence, number> = { HIGH: 0, MEDIUM: 1, LOW: 2, UNKNOWN: 3 };
const iucnOrder: Record<string, number> = { CR: 0, EN: 1, VU: 2, NT: 3 };

const SiteSpeciesRiskModal = (props: Props) => {
    const { onClose, siteId, initialTab } = props;
    const [activeTab, setActiveTab] = useState<'iucn' | 'invasives'>(initialTab);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        rowsPerPage: 10,
        // The metadata column carries the raw species for the sort functions;
        // it renders nothing.
        hiddenColumnIds: new Set(['metadataRow']),
    });
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);

    // Normalised by useSiteSpeciesRisk, so the modal renders one shape whether its
    // species came from the projection or the grid.
    const {
        siteName,
        area: areaKm2,
        country,
        redListSpecies,
        invasiveSpecies,
        isFetching,
    } = useSiteSpeciesRisk({
        siteId,
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
    });

    const speciesData = useMemo(() => ({ redListSpecies, invasiveSpecies }), [redListSpecies, invasiveSpecies]);

    const { sortedIucnSpecies, sortedInvasiveSpecies } = useMemo(() => {
        const iucnSpecies = speciesData.redListSpecies;
        const invasiveSpecies = speciesData.invasiveSpecies;

        const sortedIucnSpecies = iucnSpecies.sort((speciesA, speciesB) => {
            // Sort by IUCN category first (CR, EN, VU, NT), then by confidence
            const iucnOrderA = iucnOrder[(speciesA.iucnRedListCategory || '').toUpperCase()] ?? 99;
            const iucnOrderB = iucnOrder[(speciesB.iucnRedListCategory || '').toUpperCase()] ?? 99;
            if (iucnOrderA !== iucnOrderB) return iucnOrderA - iucnOrderB;

            // If same IUCN category, then sort by confidence
            const confidenceA = confidenceOrder[speciesA.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99;
            const confidenceB = confidenceOrder[speciesB.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99;
            return confidenceA - confidenceB;
        });

        const sortedInvasiveSpecies = invasiveSpecies.sort((speciesA, speciesB) => {
            const confidenceA = confidenceOrder[speciesA.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99;
            const confidenceB = confidenceOrder[speciesB.predictionLevel.toUpperCase() as PortfolioSpeciesConfidence] ?? 99;
            return confidenceA - confidenceB;
        });

        return { sortedIucnSpecies, sortedInvasiveSpecies };
    }, [speciesData.redListSpecies, speciesData.invasiveSpecies]);

    const displaySpeciesList = activeTab === 'iucn' ? sortedIucnSpecies : sortedInvasiveSpecies;
    const noResultsMessage =
        activeTab === 'iucn'
            ? 'There are no IUCN Red List species predicted to be present at this site'
            : 'There are no invasive species predicted to be present at this site';

    useEffect(() => {
        const tableData: DataTableState['data'] = [];

        displaySpeciesList.forEach(species => {
            const speciesName = (
                <div className='flex gap-3'>
                    <div className='flex-shrink-0'>
                        <SpeciesImage
                            scientificName={species.scientificName}
                            alt={species.commonName || species.scientificName}
                            width='84px'
                            height='47px'
                        />
                    </div>
                    <div className='flex flex-col justify-center'>
                        <div className='flex items-center gap-2'>
                            <span className='text-sm'>{species.commonName}</span>
                            {species.isNew && (
                                <QuestionButtonHelp
                                    icon={
                                        <Badge className='text-xs text-black font-normal bg-blue-20 hover:bg-blue-20 border border-blue-60'>
                                            New
                                        </Badge>
                                    }
                                    type='api'
                                    placement='top'
                                    slug='new-species-prediction-badge'
                                    zIndex={1300}
                                    maxWidth='400px'
                                />
                            )}
                        </div>
                        <div className='text-xs text-grey-80'>{species.scientificName}</div>
                    </div>
                </div>
            );

            const confidenceBadge = (
                <Badge className={confidenceDetails[species.predictionLevel.toLowerCase()].colorClass}>
                    {confidenceDetails[species.predictionLevel.toLowerCase()].label}
                </Badge>
            );

            // Display 'Detected/Undetected' badge depending on whether species found in Uploaded data
            // Display 'Upload data' badge if there is no uploaded data
            let uploadedDataBadgeType = DetectionBadgeType.UploadData;
            if (species.hasUploadedData) {
                uploadedDataBadgeType = species.foundInUploadedData ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
            }

            // Display 'Detected/Undetected' badge depending on whether species found in NM Samples
            // Display 'Book a call' badge if there is no NM Samples data
            let nmSamplesBadgeType = DetectionBadgeType.BookCall;
            if (species.hasEDNAData) {
                nmSamplesBadgeType = species.foundInEDNA ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
            }

            // Column 0 carries the raw species so the sort functions have real
            // values to compare — every visible cell is a rendered node.
            const tableRow: DataTableState['data'][number] = [{ data: species }, speciesName];

            if (activeTab === 'iucn') {
                tableRow.push(
                    <IUCNStatusChip
                        status={(species.iucnRedListCategory || '').toUpperCase() as IUCNStatusCode}
                        className={confidenceDetails.low.colorClass}
                    />
                );
            }

            tableRow.push(confidenceBadge, nmSamplesBadgeType, uploadedDataBadgeType);

            tableData.push(tableRow);
        });
        // sortedColumn cleared with the page: a sort chosen on one tab means
        // nothing on the other (the invasive tab has no IUCN column), so a
        // fresh tab starts from its default severity/confidence order.
        setTableState(prevState => ({ ...prevState, data: tableData, currentPageNumber: 1, sortedColumn: null }));
    }, [displaySpeciesList, activeTab]);

    const tableProps = {
        state: tableState,
        columns: activeTab === 'iucn' ? tableColumns : [...tableColumns].filter(column => column.columnId !== 'iucnRedListStatus'),
        onStateChange: setTableState,
        emptyStateMessage: noResultsMessage,
        className: 'overflow-visible',
        tableClassName: 'h-full',
    };

    return (
        <Modal dismissOnClickOutside={true} classNames={{ body: 'w-[950px] h-[600px] p-6' }} onClose={onClose}>
            {isFetching ? (
                <div className='flex items-center justify-center h-full'>
                    <Loader />
                </div>
            ) : (
                <div className='flex flex-col gap-4 h-full'>
                    <div className='flex items-center justify-between'>
                        <div className='flex flex-col gap-1'>
                            <div className='text-3xl text-primary'>{siteName}</div>
                            <div className='text-base text-grey-80'>Species risk indicators</div>
                        </div>
                    </div>

                    <div className='flex flex-row gap-8'>
                        <div className='flex flex-col gap-1 justify-center'>
                            <div className='flex flex-col text-sm text-grey-80'>
                                <div className='flex'>Country</div>
                            </div>
                            <div className='justify-center text-xl text-black'>{country}</div>
                        </div>
                        <div className='flex flex-col gap-1 justify-center'>
                            <div className='flex flex-col justify-center text-sm text-grey-80'>
                                <div className='flex'>
                                    Size{' '}
                                    <QuestionButtonHelp
                                        icon={<CircleQuestionMark className='text-grey-80 h-4' />}
                                        type='api'
                                        placement='right'
                                        slug='species-risk-site-size'
                                        zIndex={1300}
                                    />
                                </div>
                            </div>
                            <div className='justify-center text-xl text-black'>{areaKm2?.toFixed(2)} km²</div>
                        </div>
                    </div>

                    <div className='border-t border-grey-20 h-[1px]'>&nbsp;</div>

                    <div className='flex flex-col gap-1'>
                        <div className='text-xl text-black'>Site Presence Assessment</div>
                        <div className='text-sm text-grey-80'>{siteName} overlaps with the following predicted species ranges.</div>
                    </div>

                    <div className='flex flex-col gap-0 pb-[80px]'>
                        <div className='bg-grey-0 w-full rounded-md'>
                            <Tabs
                                size='large'
                                defaultValue={activeTab}
                                className='w-[850px]'
                                onValueChange={tab => setActiveTab(tab as 'iucn' | 'invasives')}
                            >
                                <TabsList>
                                    <TabsTrigger value='invasives'>
                                        <div className='flex gap-2 text-sm'>
                                            <CircleAlert className='text-red-100 h-5 w-5' /> Invasive species
                                        </div>
                                    </TabsTrigger>
                                    <TabsTrigger value='iucn'>
                                        <div className='flex gap-2 text-sm'>
                                            <img src={IUCNLogo} alt='' className='w-5 h-5' /> IUCN Red List species
                                        </div>
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                        <DataTable {...tableProps} />
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default SiteSpeciesRiskModal;
